import React, { useState, useMemo } from "react";
import {
  Button,
  styled,
  useMediaQuery,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Box,
  Grid,
  Typography,
  Chip,
} from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { ALL_LEADS_COLUMNS } from "./leadsData";
import { MaterialReactTable } from "material-react-table";
import { useSelector } from "react-redux";
import { selectCurrentAgent } from "../../redux/features/authSlice";
import {
  useGetAgentsQuery,
  useGetLeadsQuery,
  useGetSingleAgentLeadsQuery,
  useStatusQuery,
} from "../../redux/api/leadsApi";
import {
  CLOSE_DEAL_DONE_TYPE,
  CLOSE_UNSUCCESSFULL_TYPE,
} from "../../constants/leads";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import { isDisabled } from "../../helper/agentRights";

// ✅ Subcomponent: LeadTable
const LeadTable = ({ leads, isLoading, columns }) => {
  const isMobileScreen = useMediaQuery("(max-width:768px)");

  const getRowBackgroundColor = (leadStatus) => {
    if (leadStatus === CLOSE_DEAL_DONE_TYPE) return "#d4edda"; // green
    if (leadStatus === CLOSE_UNSUCCESSFULL_TYPE) return "#f8d7da"; // red
    return "";
  };

  return (
    <MaterialReactTable
      enableRowActions={false}
      columns={columns}
      data={leads}
      getRowId={(row) => row.leadid}
      state={{ showSkeletons: isLoading }}
      initialState={{ density: "compact", pagination: { pageSize: 20 } }}
      enableDensityToggle={false}
      enableStickyHeader={!isMobileScreen}
      muiTableContainerProps={{
        sx: { maxHeight: isMobileScreen ? "100%" : "calc(100vh - 320px)" },
      }}
      muiTableBodyRowProps={({ row }) => ({
        sx: { backgroundColor: getRowBackgroundColor(row.original.leadStatus) },
      })}
    />
  );
};

// ✅ Main Component
export default function AllLead() {
  const currentAgent = useSelector(selectCurrentAgent);
  const leadsPermissions = currentAgent?.leadsrights?.[0] || {};
  const { data: allAgents = [] } = useGetAgentsQuery(currentAgent?.agencyid);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const filter = queryParams.get("filter");
  const fromDate = queryParams.get("fromDate");
  const toDate = queryParams.get("toDate");
  const leadStatus = queryParams.get("status");
  const leadId = queryParams.get("leadId");
  const agentId = queryParams.get("agentid");
  const agentRights = currentAgent.agentrights || {};
  const canViewAllLeads = !isDisabled(agentRights, "View Agency Leads");

  const {
  data: agencyLeadsData = [],
  isLoading: isAgencyLeadsLoading,
  refetch: refetchAgencyLeads,
} = useGetLeadsQuery({ agencyId: currentAgent.agencyid });

const {
  data: singleAgentLeadsData = [],
  isLoading: isSingleLeadsLoading,
  refetch: refetchSingleAgentLeads,
} = useGetSingleAgentLeadsQuery( currentAgent.agentid );

const agencyLeads = canViewAllLeads ? agencyLeadsData : singleAgentLeadsData;
const LeadsLoading = canViewAllLeads ? isAgencyLeadsLoading : isSingleLeadsLoading;
const refetch = canViewAllLeads ? refetchAgencyLeads : refetchSingleAgentLeads;


  const { data: STATUS = [] } = useStatusQuery();

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDealFilter, setSelectedDealFilter] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");

  // ✅ Unified Filtering Logic
  const filteredLeads = useMemo(() => {
    let leads =
      agencyLeads?.filter((lead) => {
        const matchesStatus = leadStatus
          ? lead.leadStatus?.toString() === leadStatus
          : true;
        const matchesAgent = agentId
          ? lead.agentid?.toString() === agentId
          : true;
        const matchesLeadId = leadId
          ? lead.leadid?.toString() === leadId
          : true;

        let dateFilter = true;

        if (filter === "today") {
          const today = new Date();
          const updated = new Date(lead.leadUpdatedDate);

          dateFilter =
            today.getFullYear() === updated.getFullYear() &&
            today.getMonth() === updated.getMonth() &&
            today.getDate() === updated.getDate();
        } else if (filter === "custom" && fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);

          // Normalize to cover the entire "to" day
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);

          const updated = new Date(lead.leadUpdatedDate);
          dateFilter = updated >= from && updated <= to;
        } else if (filter === "Overdue") {
          // Overdue = follow-up date is before now
          const now = new Date();

          // make sure followupdate exists and is a valid date
          if (lead.followupdate) {
            const followup = new Date(lead.followupdate);

            // Only include if follow-up date is valid and in the past
            if (!isNaN(followup.getTime())) {
              dateFilter = followup < now;
            } else {
              // invalid date, skip this lead
              dateFilter = false;
            }
          } else {
            // no follow-up date, skip this lead
            dateFilter = false;
          }
        }

        return matchesStatus && matchesAgent && matchesLeadId && dateFilter;
      }) || [];

    // ✅ Hide closed leads (successful + unsuccessful) by default
    if (selectedStatus === "all" && selectedDealFilter === "all") {
      leads = leads.filter(
        (lead) =>
          lead.leadStatus !== CLOSE_DEAL_DONE_TYPE &&
          lead.leadStatus !== CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    // ✅ Apply Status Filter
    if (selectedStatus !== "all") {
      leads = leads.filter(
        (lead) => Number(lead.leadStatus) === Number(selectedStatus)
      );
    }

    // ✅ Apply Deal Filter
    if (selectedDealFilter === "successful") {
      leads = leads.filter((lead) => lead.leadStatus === CLOSE_DEAL_DONE_TYPE);
    } else if (selectedDealFilter === "unsuccessful") {
      leads = leads.filter(
        (lead) => lead.leadStatus === CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    // ✅ 🆕 Apply Agent Filter (same as first file)
    if (selectedAgent !== "all") {
      leads = leads.filter(
        (lead) => Number(lead.agentid) === Number(selectedAgent)
      );
    }

    return leads;
  }, [
    agencyLeads,
    leadStatus,
    agentId,
    leadId,
    filter,
    fromDate,
    toDate,
    selectedStatus,
    selectedDealFilter,
    selectedAgent,
  ]);

  // ✅ Column Permission Filter
  const normalizeKey = (key) =>
    key
      ?.toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");

  const filteredColumns = ALL_LEADS_COLUMNS(refetch).filter((col) => {
    const key = normalizeKey(col.accessorKey);
    if (!key) return true;
    const matchedPermission = Object.keys(leadsPermissions).find(
      (permKey) => normalizeKey(permKey) === key
    );
    return matchedPermission
      ? leadsPermissions[matchedPermission] !== false
      : true;
  });

  // ✅ Export Function
  const handleExport = () => {
    if (!filteredLeads?.length) {
      alert("No leads data available to export!");
      return;
    }

    let columns = filteredColumns.filter(
      (col) =>
        col.accessorKey &&
        !["edit", "history", "propertydata"].includes(col.accessorKey)
    );

    const hasDescription = columns.some(
      (col) => col.accessorKey === "description"
    );
    if (!hasDescription) {
      columns.push({ accessorKey: "description", header: "Description" });
    }

    columns.unshift({ accessorKey: "agentname", header: "Agent Name" });

    const exportData = filteredLeads.map((lead) => {
      const row = {};
      columns.forEach((col) => {
        const key = col.accessorKey;
        let value = lead[key];

        if (key === "leadStatus" || key === "status")
          value = lead.statusName || "";
        if (key === "agentname") value = lead.agentname || "";
        if (key === "description") value = lead.description || "";
        if (["leadTime", "followupdate", "leadUpdatedDate"].includes(key))
          value = value ? new Date(value).toLocaleString() : "";
        if (key === "firstname")
          value = `${lead.firstname || ""} ${lead.lastname || ""}`.trim();
        if (key === "leadTag") value = `L-${lead.leadid}`;
        row[col.header] = value ?? "";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "AllLeadsData.xlsx");
  };

  // ✅ Render
  return (
    <div>
      {/* Header */}
      <StyledHeader>
        <Box>
          <Typography variant="h4" fontWeight="600">
            Leads Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track your leads efficiently
          </Typography>
        </Box>
      </StyledHeader>

      {/* Filters */}
      <Box mb={2}>
        <FilterHeader>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FilterList color="primary" />
            <Typography variant="h6" sx={{ ml: 1 }}>
              Filters
            </Typography>
          </Box>
          <Chip
            label={`${filteredLeads.length} leads`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </FilterHeader>

        <FilterContent>
          {/* 🟡 Agent Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              label="Agent"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              <MenuItem value="all">All Agents</MenuItem>
              {allAgents
                ?.filter((agent) => agent.status === 1)
                .map((agent) => (
                  <MenuItem key={agent.agentid} value={agent.agentid}>
                    {agent.agentname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* 🟢 Lead Status Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Lead Status</InputLabel>
            <Select
              label="Lead Status"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                if (selectedDealFilter !== "all") setSelectedDealFilter("all");
              }}
            >
              <MenuItem value="all">Total Leads</MenuItem>
              {STATUS.map((status) => (
                <MenuItem key={status.statusId} value={status.statusId}>
                  {status.statusName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 🟣 Deal Type Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Deal Status</InputLabel>
            <Select
              label="Deal Status"
              value={selectedDealFilter}
              onChange={(e) => {
                setSelectedDealFilter(e.target.value);
                if (selectedStatus !== "all") setSelectedStatus("all");
              }}
            >
              <MenuItem value="all">Closed Leads</MenuItem>
              <MenuItem value="successful">Won Leads</MenuItem>
              <MenuItem value="unsuccessful">Lost Leads</MenuItem>
            </Select>
          </FormControl>

          <Grid item>
            <Button
              variant="contained"
              size="small"
              sx={{ color: "white" }}
              onClick={handleExport}
            >
              Export All Leads
            </Button>
          </Grid>
        </FilterContent>
      </Box>

      {/* Table */}
      <TableContainer>
        <LeadTable
          columns={filteredColumns}
          leads={filteredLeads}
          isLoading={LeadsLoading}
        />
      </TableContainer>
    </div>
  );
}

// ✅ Styling
const filterSx = {
  minWidth: 140,
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiSelect-select": { py: 0.5, fontSize: 13, height: "28px" },
  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
};

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const FilterContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

const TableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
}));
